import { useState, useMemo } from 'react'
import type { Finding, FindingSeverity, FindingCategory } from '../api/types'

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface FindingsPanelProps {
  findings: Finding[]
  loading?: boolean
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SEVERITY_OPTIONS: Array<{ value: FindingSeverity | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'critical', label: 'Critical' },
  { value: 'warning', label: 'Warning' },
  { value: 'info', label: 'Info' },
]

const CATEGORY_OPTIONS: Array<{ value: FindingCategory | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'bandwidth', label: 'Bandwidth' },
  { value: 'latency', label: 'Latency' },
  { value: 'wifi', label: 'WiFi' },
  { value: 'app', label: 'App' },
  { value: 'gateway', label: 'Gateway' },
  { value: 'isp', label: 'ISP' },
  { value: 'security', label: 'Security' },
]

const SEVERITY_CONFIG: Record<FindingSeverity, { color: string; border: string; bg: string; icon: string }> = {
  critical: {
    color: 'text-red-400',
    border: 'border-l-red-400',
    bg: 'bg-red-500/15 border-red-500/30',
    icon: 'M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z',
  },
  warning: {
    color: 'text-amber-400',
    border: 'border-l-amber-400',
    bg: 'bg-amber-500/15 border-amber-500/30',
    icon: 'M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z',
  },
  info: {
    color: 'text-blue-400',
    border: 'border-l-blue-400',
    bg: 'bg-blue-500/15 border-blue-500/30',
    icon: 'm11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z',
  },
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

/** Inline SVG icon sized for text-xs context. */
function SeverityIcon({ severity }: { severity: FindingSeverity }) {
  const { color, icon } = SEVERITY_CONFIG[severity]
  return (
    <svg
      className={`w-3.5 h-3.5 shrink-0 ${color}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d={icon} />
    </svg>
  )
}

/** Tiny pill badge used for source and category labels. */
function PillBadge({ label, className }: { label: string; className?: string }) {
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium leading-none ${className ?? 'bg-slate-700/60 text-slate-400'}`}
    >
      {label}
    </span>
  )
}

/** Skeleton shimmer bar for loading state. */
function SkeletonBar() {
  return (
    <div className="border border-slate-700 rounded-lg p-3 bg-slate-800/50 animate-pulse">
      <div className="flex items-center gap-2">
        <div className="w-3.5 h-3.5 rounded-full bg-slate-700" />
        <div className="h-3 w-32 rounded bg-slate-700" />
        <div className="ml-auto h-3 w-16 rounded bg-slate-700" />
      </div>
      <div className="mt-2 h-2.5 w-3/4 rounded bg-slate-700/60" />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Finding card
// ---------------------------------------------------------------------------

function FindingCard({
  finding,
  expanded,
  onToggle,
}: {
  finding: Finding
  expanded: boolean
  onToggle: () => void
}) {
  const cfg = SEVERITY_CONFIG[finding.severity]
  const devices = finding.affectedDevices ?? []

  return (
    <button
      type="button"
      onClick={onToggle}
      className={`
        w-full text-left border border-slate-700 rounded-lg bg-slate-800/50
        border-l-2 ${cfg.border}
        hover:bg-slate-700/40 transition-colors cursor-pointer
        focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-500
      `}
    >
      {/* Collapsed row */}
      <div className="flex items-start gap-2 p-3">
        <SeverityIcon severity={finding.severity} />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className={`text-xs font-medium ${cfg.color}`}>
              {finding.technicalLabel}
            </span>
            <PillBadge label={finding.source} />
            <PillBadge
              label={finding.category}
              className="bg-slate-700/40 text-slate-500"
            />
          </div>
          <p className="text-xs text-slate-400 mt-0.5 line-clamp-1">
            {finding.impactSummary}
          </p>
        </div>

        {/* Chevron */}
        <svg
          className={`w-4 h-4 shrink-0 text-slate-500 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
        </svg>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div
          className="px-3 pb-3 pt-0 border-t border-slate-700/50 space-y-2"
          /* Stop click from toggling when user selects text or clicks a link */
          onClick={(e) => e.stopPropagation()}
        >
          {/* Description */}
          <p className="text-xs text-slate-300 whitespace-pre-line">
            {finding.description}
          </p>

          {/* Why it matters */}
          {finding.whyItMatters && (
            <div>
              <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
                Why it matters
              </span>
              <p className="text-xs text-slate-400 mt-0.5">
                {finding.whyItMatters}
              </p>
            </div>
          )}

          {/* How to resolve */}
          <div>
            <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
              How to resolve
            </span>
            <p className="text-xs text-slate-300 mt-0.5 whitespace-pre-line">
              {finding.howToResolve}
            </p>
          </div>

          {/* Technical details */}
          {finding.technicalDetails && (
            <div>
              <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
                Technical details
              </span>
              <p className="text-xs text-slate-400 mt-0.5 font-mono whitespace-pre-line">
                {finding.technicalDetails}
              </p>
            </div>
          )}

          {/* Affected devices */}
          {devices.length > 0 && (
            <div>
              <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
                Affected devices ({devices.length})
              </span>
              <ul className="mt-1 space-y-0.5">
                {devices.map((d) => (
                  <li
                    key={d.mac}
                    className="flex items-center gap-2 text-xs text-slate-400 font-mono"
                  >
                    <span className="text-slate-500">{d.mac}</span>
                    {d.hostname && (
                      <span className="text-slate-300">{d.hostname}</span>
                    )}
                    {d.ouiVendor && (
                      <span className="text-slate-500">({d.ouiVendor})</span>
                    )}
                    {d.ip && (
                      <span className="text-slate-600">{d.ip}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Learn more link */}
          {finding.learnMoreURL && (
            <a
              href={finding.learnMoreURL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[10px] text-blue-400 hover:text-blue-300 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              Learn more
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
              </svg>
            </a>
          )}
        </div>
      )}
    </button>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function FindingsPanel({ findings, loading }: FindingsPanelProps) {
  const [severityFilter, setSeverityFilter] = useState<FindingSeverity | 'all'>('all')
  const [categoryFilter, setCategoryFilter] = useState<FindingCategory | 'all'>('all')
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  const filtered = useMemo(() => {
    const severityRank: Record<string, number> = { critical: 0, warning: 1, info: 2 }
    return findings
      .filter((f) => {
        if (severityFilter !== 'all' && f.severity !== severityFilter) return false
        if (categoryFilter !== 'all' && f.category !== categoryFilter) return false
        return true
      })
      .sort((a, b) => {
        const sa = severityRank[a.severity] ?? 3
        const sb = severityRank[b.severity] ?? 3
        if (sa !== sb) return sa - sb
        // Secondary: newest first
        return (b.createdAt ?? '').localeCompare(a.createdAt ?? '')
      })
  }, [findings, severityFilter, categoryFilter])

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  // -----------------------------------------------------------------------
  // Loading state
  // -----------------------------------------------------------------------
  if (loading) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
            Findings
          </span>
        </div>
        <SkeletonBar />
        <SkeletonBar />
        <SkeletonBar />
      </div>
    )
  }

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  return (
    <div className="space-y-2">
      {/* Header + filters */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
          Findings
          {filtered.length > 0 && (
            <span className="ml-1 text-slate-600">({filtered.length})</span>
          )}
        </span>

        <div className="flex items-center gap-1.5">
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value as FindingSeverity | 'all')}
            className="text-[10px] bg-slate-700 text-slate-300 border border-slate-600 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-slate-500"
          >
            {SEVERITY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value as FindingCategory | 'all')}
            className="text-[10px] bg-slate-700 text-slate-300 border border-slate-600 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-slate-500"
          >
            {CATEGORY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Empty state */}
      {filtered.length === 0 && (
        <p className="text-xs text-slate-500 py-4 text-center">No findings</p>
      )}

      {/* Finding cards */}
      {filtered.map((f) => (
        <FindingCard
          key={f.id}
          finding={f}
          expanded={expandedIds.has(f.id)}
          onToggle={() => toggleExpanded(f.id)}
        />
      ))}
    </div>
  )
}
